import React, { useEffect, useState } from 'react';
import { faCheck, faCircleCheck, faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    ApprovalState,
    FeeAgreement,
    FeeAgreementParty,
} from '../../../models/FlowOfFunds/flow-of-funds.model';
import { formatDate } from '../../../utils/flowOfFundsFormat';
import { NotificationFlag } from '../../NotificationFlag/NotificationFlag';
import '../ApprovalControl/ApprovalControl.scss';
import './FeeAgreementBlock.scss';

interface FeeAgreementBlockProps {
    agreement: FeeAgreement;
    description: string;
    /** When set, the block badges the seats with this notification-flag code. */
    seatFlagEventCode?: string;
    /** Legal Fees: per-seat control, so a seat can be agreed and un-agreed. */
    onSeatStateChange?: (party: FeeAgreementParty, targetState: ApprovalState) => void;
    /** VetValue Fees: a single agree action on the viewer's own party. */
    onAgree?: () => void;
    isSaving?: boolean;
}

const AgreementStatusBadge = ({ state }: { state: ApprovalState }) => {
    if (state === ApprovalState.Approved) {
        return (
            <span className="ff-approval__status ff-approval__status--approved">Agreed</span>
        );
    }
    if (state === ApprovalState.Rejected) {
        return (
            <span className="ff-approval__status ff-approval__status--rejected">Declined</span>
        );
    }
    return <span className="ff-approval__status">Pending</span>;
};

const AgreedMeta = ({ party }: { party: FeeAgreementParty }) =>
    party.state === ApprovalState.Approved && party.agreedByName ? (
        <span className="ff-agreement__agreed-meta">
            · {party.agreedByName}
            {party.isOnBehalf ? ' (on behalf)' : ''} · {formatDate(party.agreedOn)}
        </span>
    ) : null;

const InteractiveAgreementParty = ({
    party,
    onChange,
}: {
    party: FeeAgreementParty;
    onChange: (targetState: ApprovalState) => void;
}) => {
    const [isRevisiting, setIsRevisiting] = useState(false);

    useEffect(() => {
        setIsRevisiting(false);
    }, [party.state]);

    const hasDecided =
        party.state === ApprovalState.Approved || party.state === ApprovalState.Rejected;

    if (!party.canAct || (hasDecided && !isRevisiting)) {
        return (
            <span className="ff-agreement__agreed">
                <AgreementStatusBadge state={party.state} />
                <AgreedMeta party={party} />
                {party.canAct && (
                    <button
                        type="button"
                        title="Change your decision"
                        className="ff-approval__edit"
                        onClick={() => setIsRevisiting(true)}>
                        <FontAwesomeIcon icon={faPenToSquare} />
                    </button>
                )}
            </span>
        );
    }

    const isApproved = party.state === ApprovalState.Approved;

    return (
        <span className="ff-agreement__actions">
            <button
                type="button"
                className="btn btn-primary btn-sm ff-agreement__agree"
                onClick={() =>
                    onChange(isApproved ? ApprovalState.Pending : ApprovalState.Approved)
                }>
                {isApproved ? 'Un-agree' : 'Agree to Fee Amount'}
            </button>
        </span>
    );
};

export const FeeAgreementBlock = ({
    agreement,
    description,
    seatFlagEventCode,
    onSeatStateChange,
    onAgree,
    isSaving,
}: FeeAgreementBlockProps) => {
    const haveAllAgreed =
        agreement.parties.length > 0 && agreement.parties.every((party) => party.hasAgreed);
    const pendingParties = agreement.parties.filter((party) => !party.hasAgreed);

    return (
        <div className="ff-agreement">
            <h4 className="ff-agreement__title">
                <span className="ff-agreement__flagged">
                    Fee Agreement
                    {seatFlagEventCode && (
                        <NotificationFlag eventCode={seatFlagEventCode} doNotRemove={true} />
                    )}
                </span>
            </h4>
            <p className="ff-agreement__description">{description}</p>

            <div className="ff-agreement__parties">
                {agreement.parties.map((party) => (
                    <div key={party.label} className="ff-agreement__party">
                        <span className="ff-agreement__party-label">{party.label}</span>
                        {onSeatStateChange ? (
                            <InteractiveAgreementParty
                                party={party}
                                onChange={(targetState) => onSeatStateChange(party, targetState)}
                            />
                        ) : party.hasAgreed ? (
                            <span className="ff-agreement__agreed">
                                <FontAwesomeIcon icon={faCheck} /> Agreed
                                {party.agreedByName && (
                                    <span className="ff-agreement__agreed-meta">
                                        · {party.agreedByName} · {formatDate(party.agreedOn)}
                                    </span>
                                )}
                            </span>
                        ) : party.canAgree ? (
                            <button
                                type="button"
                                className="btn btn-primary btn-sm ff-agreement__agree"
                                disabled={isSaving}
                                onClick={onAgree}>
                                Agree to Fee Amount
                            </button>
                        ) : (
                            <span className="ff-agreement__pending">Pending</span>
                        )}
                    </div>
                ))}
            </div>

            <div className="ff-agreement__status">
                {haveAllAgreed ? (
                    <span className="ff-agreement__status--done">
                        <FontAwesomeIcon icon={faCircleCheck} /> Fees confirmed by all parties
                    </span>
                ) : (
                    <span className="ff-agreement__status--pending">
                        Awaiting {pendingParties.map((party) => party.label).join(' and ')} agreement
                    </span>
                )}
            </div>
        </div>
    );
};
